import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { API } from '@/lib/site';
import { toast } from 'sonner';

/**
 * Reusable contact form posting to /api/contact/submit.
 * Props:
 *   formType: 'discovery_call' | 'partnership' | 'general' | 'workforce_plan' | 'programs'
 *   variant: 'dark' | 'light'  (dark = on black surface, light = on white)
 *   title, subtitle
 *   showCompany, showRole, showEmployees, showIndustry, showInterest
 *   ctaLabel
 *   sourcePage
 */
export default function ContactForm({
  formType = 'general',
  variant = 'dark',
  title = 'Speak With Ball Raw Performance',
  subtitle = 'Tell us about your team. We respond within one business day.',
  showCompany = true,
  showRole = false,
  showEmployees = false,
  showIndustry = false,
  showInterest = false,
  ctaLabel = 'Submit Request',
  sourcePage = '',
  testIdPrefix = 'contact',
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    employees: '',
    industry: '',
    interest: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isDark = variant === 'dark';
  const inputCls = `brp-input ${isDark ? '' : 'brp-input-light'}`;
  const labelCls = `font-display uppercase tracking-[0.18em] text-[11px] font-bold mb-2 block ${
    isDark ? 'text-[#D4AF37]' : 'text-[#0A0A0A]'
  }`;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please fill in your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        form_type: formType,
        source_page: sourcePage || window.location.pathname,
        ...form,
      };
      await axios.post(`${API}/contact/submit`, payload);
      setSubmitted(true);
      toast.success('Request received. We will be in touch shortly.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className={`${
          isDark ? 'bg-[#111111] text-white border-[#D4AF37]/30' : 'bg-white text-[#0A0A0A] border-[#E5E5E5]'
        } border p-10 md:p-14 text-center`}
        data-testid={`${testIdPrefix}-success`}
      >
        <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto" strokeWidth={1.5} />
        <h3 className="font-display uppercase tracking-wide text-2xl mt-6">
          Request Received
        </h3>
        <p className={`mt-4 max-w-md mx-auto ${isDark ? 'text-gray-300' : 'text-[#555]'}`}>
          A Ball Raw Performance partner specialist will reach out within one business day.
        </p>
        <p className="mt-6 brp-overline">Claim Your Game.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`${
        isDark ? 'bg-[#111111] text-white border-[#D4AF37]/30' : 'bg-white text-[#0A0A0A] border-[#E5E5E5]'
      } border p-8 md:p-12 relative`}
      data-testid={`${testIdPrefix}-form`}
    >
      <div className="absolute top-0 left-0 h-1 w-24 bg-[#D4AF37]" />
      <h3 className="font-display uppercase tracking-wide text-2xl md:text-3xl">
        {title}
      </h3>
      <p className={`mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-[#555]'}`}>{subtitle}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input
            className={inputCls}
            value={form.name}
            onChange={update('name')}
            required
            data-testid={`${testIdPrefix}-input-name`}
          />
        </div>
        <div>
          <label className={labelCls}>Work Email *</label>
          <input
            type="email"
            className={inputCls}
            value={form.email}
            onChange={update('email')}
            required
            data-testid={`${testIdPrefix}-input-email`}
          />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input
            className={inputCls}
            value={form.phone}
            onChange={update('phone')}
            data-testid={`${testIdPrefix}-input-phone`}
          />
        </div>
        {showCompany && (
          <div>
            <label className={labelCls}>Company / Organization</label>
            <input
              className={inputCls}
              value={form.company}
              onChange={update('company')}
              data-testid={`${testIdPrefix}-input-company`}
            />
          </div>
        )}
        {showRole && (
          <div>
            <label className={labelCls}>Role / Title</label>
            <input
              className={inputCls}
              value={form.role}
              onChange={update('role')}
              data-testid={`${testIdPrefix}-input-role`}
            />
          </div>
        )}
        {showEmployees && (
          <div>
            <label className={labelCls}>Employees</label>
            <select
              className={inputCls}
              value={form.employees}
              onChange={update('employees')}
              data-testid={`${testIdPrefix}-input-employees`}
            >
              <option value="">Select range</option>
              <option>Under 100</option>
              <option>100 – 500</option>
              <option>500 – 2,000</option>
              <option>2,000 – 10,000</option>
              <option>10,000+</option>
            </select>
          </div>
        )}
        {showIndustry && (
          <div>
            <label className={labelCls}>Industry</label>
            <select
              className={inputCls}
              value={form.industry}
              onChange={update('industry')}
              data-testid={`${testIdPrefix}-input-industry`}
            >
              <option value="">Select industry</option>
              <option>Manufacturing</option>
              <option>Warehousing & Logistics</option>
              <option>Healthcare</option>
              <option>Technology</option>
              <option>Hospitality</option>
              <option>Education</option>
              <option>Sports Organization</option>
              <option>Other</option>
            </select>
          </div>
        )}
        {showInterest && (
          <div className="md:col-span-2">
            <label className={labelCls}>I'm Interested In</label>
            <select
              className={inputCls}
              value={form.interest}
              onChange={update('interest')}
              data-testid={`${testIdPrefix}-input-interest`}
            >
              <option value="">Select an area</option>
              <option>Corporate Games & Competitive Leagues</option>
              <option>Workforce Performance Systems</option>
              <option>Sports Performance & Mental Resilience</option>
              <option>Next Gen Academy</option>
              <option>Partnership Opportunity</option>
              <option>Custom Workforce Plan</option>
            </select>
          </div>
        )}
        <div className="md:col-span-2">
          <label className={labelCls}>Tell Us More</label>
          <textarea
            className={inputCls}
            rows={4}
            value={form.message}
            onChange={update('message')}
            placeholder="Share your goals, workforce size, or partnership idea."
            data-testid={`${testIdPrefix}-input-message`}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-brp-primary mt-8 disabled:opacity-60"
        data-testid={`${testIdPrefix}-submit-btn`}
      >
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? 'Submitting…' : ctaLabel}
      </button>
      <p className={`text-xs mt-4 ${isDark ? 'text-gray-500' : 'text-[#888]'}`}>
        By submitting, you agree to be contacted by a Ball Raw Performance specialist.
      </p>
    </form>
  );
}
